import * as React from 'react';

export interface ExpandableGridState {
    expanded: boolean;
    columns: number;
    maxVisible: number;
    gridRef: React.RefObject<HTMLDivElement>;
    showOverlay: boolean;
    showMoreButton: boolean;
    showLessButton: boolean;
    toggleExpanded: () => void;
    isCoveredByOverlay: (index: number) => boolean;
}

export const useExpandableGrid = (
    itemCount: number,
    rowsToShow: number,
): ExpandableGridState => {
    const gridRef = React.useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = React.useState(false);
    const [columns, setColumns] = React.useState(2);

    React.useLayoutEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        const updateColumns = () => {
            const gridStyle = window.getComputedStyle(grid);

            const columnCount =
                gridStyle.gridTemplateColumns === 'none'
                    ? 1
                    : gridStyle.gridTemplateColumns.trim().split(/\s+/).length;

            setColumns(columnCount);
        };

        updateColumns();

        const observer = new ResizeObserver(updateColumns);
        observer.observe(grid);

        return () => observer.disconnect();
    }, []);

    const maxVisible = rowsToShow * columns;
    const showMoreButton = !expanded && itemCount > maxVisible;
    const showLessButton = expanded && itemCount > maxVisible;

    return {
        expanded,
        columns,
        maxVisible,
        gridRef,
        showOverlay: showMoreButton,
        showMoreButton,
        showLessButton,
        toggleExpanded: () => setExpanded((previous) => !previous),
        // Items in the last visible row sit under the fade overlay, so their border is hidden.
        isCoveredByOverlay: (index: number) =>
            showMoreButton &&
            index >= maxVisible - columns &&
            index < maxVisible,
    };
};
